import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import clsx from 'clsx';
import RttRoundedIcon from '@mui/icons-material/RttRounded';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { RotatingLines } from 'react-loader-spinner';

import SlideButton from '../../../id8/components/frames/slide-button/SlideButton';
import { REACTIONS } from '../../../constants';
import CommonMenu from '../../../components/common-menu/CommonMenu';
import CommonPopper from '../../../components/common-popper/CommonPopper';
import { COMMON_POPPER_MODIFIERS } from '../../../constants/popper';
import ConfirmationModal from '../../../components/confirmation-modal/ConfirmationModal';
import CommonModal from '../../../components/common-modal/CommonModal';
import CopyButton from '../../../components/copy-button/CopyButton';
import { Input } from '../../../components/common-inputs';
import MoreActions from './MoreActions';
import useCommonModal from '../../../hooks/useCommonModal';

import scss from './ContentSlideActions.module.scss';

function ContentSlideActions({
  slide,
  onDownload,
  onDownloadSource,
  onLike,
  onPreview,
  onStudio,
  onStory,
  onDelete,
  onCopyLink,
  onEditTags,
  onRestore,
  isError,
  popperZIndex = 8999,
  disableMoreActionsPortal = false,
}) {
  const { t } = useTranslation('activ8');
  const [loadingType, setLoadingType] = useState(null);
  const [deleteMedia, setDeleteMedia] = useState(null);
  const {
    isOpen: isPublicLinksModalOpen,
    handleOpenModal: handleOpenPublicLinksModal,
    handleCloseModal: handleClosePublicLinksModal,
  } = useCommonModal();

  if (!onDownload && !onLike && !onPreview && !onRestore) return null;

  const likeIcon = slide.reaction === REACTIONS.LIKE ? FavoriteOutlinedIcon : FavoriteBorderOutlinedIcon;

  const handleDelete = () => {
    onDelete(slide);
    setDeleteMedia(null);
  };

  const handleOpenDeleteConfirmation = () => {
    setDeleteMedia(slide);
  };

  const handleDeleteRejection = () => {
    setDeleteMedia(null);
  };

  const handleLike = () => {
    onLike(slide);
  };

  const handleDownload = () => {
    onDownload(slide);
  };

  const handleDownloadSource = async () => {
    setLoadingType(LOADING_TYPES.DOWNLOAD_SOURCE);
    await onDownloadSource(slide);
    setLoadingType(null);
  };

  const handleStudio = () => {
    onStudio(slide);
  };

  const handleStory = () => {
    onStory(slide);
  };

  const handleCopyLink = (link) => {
    onCopyLink(link);
  };

  const handleDisplay = () => {
    if (onPreview) {
      onPreview(slide);
      return;
    }
  };

  const handleEditTags = () => {
    if (onEditTags) {
      onEditTags(slide);
    }
  };

  const handleRestore = () => {
    onRestore(slide);
  };

  return (
    <div className={scss.actions}>
      {onLike && (
        <SlideButton
          onClick={handleLike}
          tooltip={t('like')}
          icon={likeIcon}
          ariaLabel='Like this picture'
          className={clsx(slide.reaction === REACTIONS.LIKE && scss.favoriteSlide)}
        />
      )}
      {onStudio && !isError && (
        <SlideButton onClick={handleStudio} icon={AutoFixHighOutlinedIcon} tooltip={t('goToStudio')} />
      )}
      {onStory && !isError && <SlideButton onClick={handleStory} icon={RttRoundedIcon} tooltip={t('goToStory')} />}
      {onDownload && !isError && !onDownloadSource && (
        <SlideButton onClick={handleDownload} icon={DownloadOutlinedIcon} tooltip={t('download')} />
      )}
      {onDownload && onDownloadSource && !isError && (
        <CommonPopper
          isShowCaret={false}
          disablePortal
          options={[{ ...COMMON_POPPER_MODIFIERS, name: 'offset', options: { offset: [0, 2] } }]}
          placement='bottom-end'
          component={() => (
            <DownloadMenu
              onDownloadConverted={handleDownload}
              onDownloadSource={handleDownloadSource}
              loadingType={loadingType}
            />
          )}>
          <SlideButton icon={DownloadOutlinedIcon} />
        </CommonPopper>
      )}
      {onRestore && !isError && (
        <SlideButton onClick={handleRestore} icon={ReplayOutlinedIcon} tooltip={t('restore')} />
      )}
      {!isError && (
        <MoreActions
          onDisplay={onPreview ? handleDisplay : undefined}
          onEditTags={onEditTags ? handleEditTags : undefined}
          onPublicLinks={
            onCopyLink && (slide.contentDisplayLink || slide.contentDownloadLink)
              ? handleOpenPublicLinksModal
              : undefined
          }
          onDelete={onDelete ? handleOpenDeleteConfirmation : undefined}
          popperZIndex={popperZIndex}
          disablePortal={disableMoreActionsPortal}
        />
      )}

      <ConfirmationModal
        isOpen={deleteMedia !== null}
        onClose={handleDeleteRejection}
        onCancel={handleDeleteRejection}
        onConfirm={handleDelete}
        label={t('areYouSureYouWantToRemoveThisContent')}
      />
      <CommonModal isOpen={isPublicLinksModalOpen} onClose={handleClosePublicLinksModal}>
        <CopyLinkMenu onCopyLink={handleCopyLink} slide={slide} />
      </CommonModal>
    </div>
  );
}

export default ContentSlideActions;

const DownloadMenu = ({ onDownloadConverted, onDownloadSource, loadingType }) => {
  const { t } = useTranslation('activ8');

  const userMenuOptions = [
    {
      label: t('downloadSource'),
      value: 'downloadSource',
      onClick: onDownloadSource,
      preventWrap: true,
      disabled: loadingType === LOADING_TYPES.DOWNLOAD_SOURCE,
      icon:
        loadingType === LOADING_TYPES.DOWNLOAD_SOURCE ? <RotatingLines size={16} strokeColor='#fdbf47' /> : undefined,
    },
    {
      label: t('downloadConverted'),
      value: 'downloadConverted',
      onClick: onDownloadConverted,
      preventWrap: true,
    },
  ];

  const selectMenuOption = (selectedItem) => {
    if (selectedItem.onClick) {
      selectedItem.onClick();
    }
  };

  return <CommonMenu options={userMenuOptions} onSelect={selectMenuOption} />;
};

const CopyLinkMenu = ({ onCopyLink, slide }) => {
  const { t } = useTranslation('activ8');
  const { contentDisplayLink, contentDownloadLink } = slide;

  return (
    <div className={scss.copyLinkWrapper}>
      {contentDisplayLink ? (
        <div className={scss.copyLink}>
          <div className={scss.valueWrapper}>
            <span className={scss.copyLinkLabel}>{t('display')}:</span>
            <Input value={contentDisplayLink} isReadOnly size='sm' />
          </div>

          <CopyButton onClick={() => onCopyLink(contentDisplayLink)} />
        </div>
      ) : null}
      {contentDownloadLink ? (
        <div className={scss.copyLink}>
          <div className={scss.valueWrapper}>
            <span className={scss.copyLinkLabel}>{t('download')}:</span>
            <Input value={contentDownloadLink} isReadOnly size='sm' />
          </div>

          <CopyButton onClick={() => onCopyLink(contentDownloadLink)} />
        </div>
      ) : null}
    </div>
  );
};

const LOADING_TYPES = {
  DOWNLOAD_SOURCE: 'downloadSource',
  DOWNLOAD_CONVERTED: 'downloadConverted',
};
